import React, { useState, useEffect } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import { StatusPill } from "../components/StatusPill";
import { BreakGameModal } from "../components/BreakGameModal";
import { useApp } from "../context/AppContext";
import { formatClientDisplayTime } from "../hooks/useClock";
import { getImageUrl } from "../api/client";
import api from "../api/client";
import { isEmpAbsent, canMarkAbsent, isMarkedAbsentToday, hasCheckInToday, attendanceStatusToday } from "../utils/empStatus";
import { employeeHasWhatsApp } from "../utils/whatsapp";
import {
  AlertTriangle, MapPin, ChevronRight, TrendingUp, Star, Award, CheckCircle2,
  Clock, Calendar, Users, ShieldAlert, BarChart2, RefreshCw, Trash2, X, Gamepad2, Sparkles, Trophy, MessageCircle, UserX
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell
} from "recharts";

function formatTimeOnly(dateStr) {
  if (!dateStr) return "N/A";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      // If it's already a time string like "09:15 AM", return it
      if (typeof dateStr === "string" && (dateStr.toLowerCase().includes("am") || dateStr.toLowerCase().includes("pm"))) {
        return dateStr;
      }
      return dateStr;
    }
    return d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  } catch (e) {
    return dateStr;
  }
}

function shortIstDate() {
  return new Date().toLocaleDateString("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
  }).replace(/,/g, "");
}

function generateIndividualWhatsAppText(emp) {
  const d = new Date();
  const todayStr = d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).replace(/,/g, "");

  const todayYmd = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const attToday = attendanceStatusToday(emp);
  const onLeave = attToday === "leave" || attToday === "cl" || attToday === "ml";
  const onLop = attToday === "lop";
  const isAbsent = !onLeave && !onLop && (isEmpAbsent(emp) || isMarkedAbsentToday(emp));

  let checkIn = emp.checkInTime
    ? formatTimeOnly(emp.checkInTime)
    : (onLeave ? "Leave" : onLop ? "LOP" : isAbsent ? "Absent / LOP" : "Pending Arrival");
  let checkOut = emp.checkOutTime
    ? formatTimeOnly(emp.checkOutTime)
    : (emp.checkedIn ? "Active (Checked In)" : "N/A");

  let odDetails = "None";
  const activeOd = (emp.odHistory || []).find((o) => o.from <= todayYmd && todayYmd <= o.to);
  if (activeOd) {
    const arrTime = activeOd.arrivalTime ? formatTimeOnly(activeOd.arrivalTime) : "Pending Arrival";
    const compTime = activeOd.completedTime ? formatTimeOnly(activeOd.completedTime) : "Active OD";
    odDetails = `${activeOd.city} (Arrived: ${arrTime} | Checked-Out: ${compTime})`;
  } else if (emp.onOD) {
    odDetails = emp.odCity || "On Duty";
  }

  let reportStatus = "Report Missing";
  const todayReport = (emp.reports || []).find((r) => r.date === todayYmd);
  if (todayReport || emp.hasSubmittedReportToday) {
    const at = todayReport?.submittedAt ? formatTimeOnly(todayReport.submittedAt) : "";
    reportStatus = at && at !== "N/A" ? `Report Sent at ${at}` : "Report Sent";
  }

  let attStatus = "Present";
  if (emp.onOD) attStatus = "On On-Duty (OD)";
  else if (onLeave) attStatus = "Leave";
  else if (onLop) attStatus = "LOP";
  else if (isAbsent) attStatus = "Absent / LOP";
  else if (!emp.checkedIn && !emp.checkOutTime && !hasCheckInToday(emp)) attStatus = "Pending Arrival";

  let text = `📢 GoldPE WIBIL Daily Status Update\n`;
  text += `📅 Date: ${todayStr}\n`;
  text += `👤 Employee: ${emp.name} (ID: ${emp.employeeId || emp.employee_id})\n\n`;
  text += `🔹 Morning Check-In: ${checkIn}\n`;
  text += `   Morning Check-Out: ${checkOut}\n`;
  text += `🔸 OD Trip: ${odDetails}\n`;
  text += `📝 Daily Report: ${reportStatus}\n`;
  text += `📋 Attendance Status: ${attStatus}\n\n`;
  text += `Please verify and reply if there are any discrepancies.`;

  return text.trim();
}

export function OverviewScreen({ onSelectEmp, onNavigate }) {
  const { team, tasks, fetchTodayLunchRequests, refreshTeam, markEmployeeAbsent } = useApp();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gameModalOpen, setGameModalOpen] = useState(false);
  const [gameLeaderboard, setGameLeaderboard] = useState([]);
  const [lunchRequests, setLunchRequests] = useState(0);
  const [incidentCounts, setIncidentCounts] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, solved: 0, unsolved: 0 });
  const [sendingId, setSendingId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [absentingId, setAbsentingId] = useState(null);
  const [waNotice, setWaNotice] = useState("");
  const [lopRequests, setLopRequests] = useState([]);
  const [lopLoadingId, setLopLoadingId] = useState(null);

  const fetchLopRequests = async () => {
    try {
      const res = await api.get("/checkin/cancel-lop-requests/pending");
      if (res.data && res.data.data) {
        setLopRequests(res.data.data);
      }
    } catch (e) {
      console.error("Error fetching LOP requests:", e);
    }
  };

  const handleReviewLop = async (id, status) => {
    setLopLoadingId(id);
    try {
      await api.post(`/checkin/cancel-lop-requests/${id}/review`, { status });
      setWaNotice(`LOP cancellation request ${status} successfully.`);
      fetchLopRequests();
      if (refreshTeam) refreshTeam();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update LOP cancellation request.");
    } finally {
      setLopLoadingId(null);
    }
  };

  const handleSendStatus = async (emp) => {
    const hasWhatsapp = employeeHasWhatsApp(emp);
    const id = emp.employee_id || emp.employeeId || emp.id;
    if (!hasWhatsapp) {
      setWaNotice(`${emp.name} has no WhatsApp number.`);
      return;
    }
    setSendingId(id);
    setWaNotice("");
    try {
      const res = await api.post(`/employees/${id}/send-daily-status`);
      if (res.data?.ok) {
        setWaNotice(`Status sent to ${emp.name}.`);
      } else if (res.data?.waUrl) {
        window.open(res.data.waUrl, "_blank");
        setWaNotice(`Opened WhatsApp for ${emp.name}.`);
      } else if (res.data?.skipped) {
        setWaNotice(`${emp.name}: No WhatsApp number.`);
      } else {
        const fallback = generateIndividualWhatsAppText(emp);
        const phone = emp.phone ? emp.phone.replace(/[^0-9]/g, "") : "";
        const targetPhone = phone.length === 10 ? `91${phone}` : phone;
        window.open(`https://api.whatsapp.com/send?phone=${targetPhone}&text=${encodeURIComponent(fallback)}`, "_blank");
      }
    } catch (err) {
      const fallback = generateIndividualWhatsAppText(emp);
      const phone = emp.phone ? emp.phone.replace(/[^0-9]/g, "") : "";
      const targetPhone = phone.length === 10 ? `91${phone}` : phone;
      if (targetPhone) {
        window.open(`https://api.whatsapp.com/send?phone=${targetPhone}&text=${encodeURIComponent(fallback)}`, "_blank");
      } else {
        setWaNotice(err.response?.data?.error || `Failed to send status to ${emp.name}.`);
      }
    } finally {
      setSendingId(null);
    }
  };

  const handleSendAll = async () => {
    const activeEmployees = team.filter((e) => (e.role || "").toLowerCase() !== "manager" && !e.is_disabled);
    setSendingAll(true);
    setWaNotice("");
    try {
      const res = await api.post("/employees/send-daily-status-all");
      if (res.data?.cloud && res.data.sentCount > 0) {
        setWaNotice(`Sent ${res.data.sentCount} WhatsApp updates. Skipped (No WA): ${res.data.skipped}.`);
      } else {
        activeEmployees.filter((e) => employeeHasWhatsApp(e)).forEach((emp, index) => {
          setTimeout(() => handleSendStatus(emp), index * 900);
        });
        if (res.data?.message) setWaNotice(res.data.message);
      }
    } catch (err) {
      activeEmployees.filter((e) => employeeHasWhatsApp(e)).forEach((emp, index) => {
        setTimeout(() => handleSendStatus(emp), index * 900);
      });
    } finally {
      setSendingAll(false);
    }
  };

  const handleMarkAbsent = async (emp) => {
    if (!canMarkAbsent(emp)) return;
    const id = emp.employee_id || emp.employeeId || emp.id;
    if (!confirm(`Mark ${emp.name} as Absent for today?`)) return;
    setAbsentingId(id);
    try {
      await markEmployeeAbsent(id);
      setWaNotice(`${emp.name} marked Absent.`);
    } catch (err) {
      alert(err.response?.data?.error || `Failed to mark ${emp.name} absent.`);
    } finally {
      setAbsentingId(null);
      if (refreshTeam) refreshTeam();
    }
  };

  const fetchGameLeaderboard = async () => {
    try {
      const res = await api.get("/game/leaderboard?gameMode=gold_rush");
      if (res.data && res.data.leaderboard) {
        setGameLeaderboard(res.data.leaderboard);
      }
    } catch (e) {}
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("fp_token");
      if (!token) return;
      const res = await fetch("https://api.goldpe.tech/api/analytics/performance", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalyticsData(data);
      }
    } catch (err) {
      console.error("Failed to load analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchIncidentCounts = async () => {
    try {
      const token = localStorage.getItem("fp_token");
      if (!token) return;
      const res = await fetch("https://api.goldpe.tech/api/incidents", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const all = data.data || [];
        const pending  = all.filter(i => (i.manager_status || "pending").toLowerCase() === "pending").length;
        const approved = all.filter(i => (i.manager_status || "").toLowerCase() === "approved").length;
        const rejected = all.filter(i => (i.manager_status || "").toLowerCase() === "rejected").length;
        const solved   = all.filter(i => i.has_solution === 1 || i.has_solution === true).length;
        const unsolved = all.length - solved;
        setIncidentCounts({ total: all.length, pending, approved, rejected, solved, unsolved });
      }
    } catch (err) {
      console.error("Failed to load incidents:", err);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    fetchGameLeaderboard();
    fetchIncidentCounts();
    fetchLopRequests();
    if (fetchTodayLunchRequests) {
      fetchTodayLunchRequests().then(res => {
        if (res && res.count !== undefined) setLunchRequests(res.count);
      }).catch(() => {});
    }
  }, [team, fetchTodayLunchRequests]);

  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const checkedInCount   = team.filter((e) => e.checkedIn).length;
  const onODCount        = team.filter((e) => e.onOD).length;
  const dailyReportCount = team.filter((e) => e.hasSubmittedReportToday || (e.reports && e.reports.some(r => r.date === today))).length;
  const absentCount      = team.filter((e) => isEmpAbsent(e)).length;
  const checkedOutCount  = team.filter((e) => !e.checkedIn && !e.onOD && !!e.checkOutTime).length;

  // Fallback stats if API loading
  const summary = analyticsData?.summary || {
    totalEmployees: team.length,
    activeCheckedIn: checkedInCount,
    avgPerformanceScore: team.length ? Math.round(team.reduce((s, e) => s + (e.score || 0), 0) / team.length) : 85,
    avgManagerRating: 4.8,
    taskCompletionRate: 92,
    punctualityRate: 88,
    totalTasksAssigned: team.reduce((s, e) => s + (e.tasksToday?.total || 0), 0),
    totalTasksCompleted: team.reduce((s, e) => s + (e.tasksToday?.done || 0), 0),
    totalReportsSubmitted: dailyReportCount,
  };

  const employeesPerformance = analyticsData?.employeesPerformance || team.map(emp => ({
    employeeId: emp.employee_id || emp.id,
    name: emp.name,
    role: emp.role,
    score: emp.score || 85,
    avgRating: 4.8,
    ratingsCount: 5,
    taskCompletionRate: emp.tasksToday?.total ? Math.round((emp.tasksToday.done / emp.tasksToday.total) * 100) : 100,
    tasksAssigned: emp.tasksToday?.total || 0,
    tasksCompleted: emp.tasksToday?.done || 0,
    punctualityRate: emp.checkedIn ? 100 : 80,
    reportsSubmitted: emp.hasSubmittedReportToday ? 1 : 0,
    performanceTier: (emp.score || 85) >= 90 ? "Top Performer 🏆" : (emp.score || 85) >= 80 ? "High Achiever ⭐" : "Consistent 👍",
  }));

  const ratingDist = analyticsData?.analyticsRating?.ratingDistribution || {
    fiveStar: 18, fourStar: 6, threeStar: 2, twoStar: 0, oneStar: 0
  };
  const totalRatings = Object.values(ratingDist).reduce((a, b) => a + b, 0) || 1;

  const punctuality = analyticsData?.punctualityOverview || {
    onTimeCount: checkedInCount, graceCount: 1, lateCount: absentCount,
    totalInCheckins: team.length || 1,
    onTimePercentage: Math.round((checkedInCount / (team.length || 1)) * 100),
    gracePercentage: 10, latePercentage: 10, averageDelayMinutes: 12
  };

  // City distribution
  const cityMap = {};
  team.forEach((e) => {
    const city = (e.onOD ? e.odCity : e.lastLocation?.split(",")[0]) || "Main Branch";
    cityMap[city] = (cityMap[city] || 0) + 1;
  });
  const cityData = Object.entries(cityMap).map(([city, count]) => ({ city, count }));

  // Dismissable Alerts State
  const [clearedAlerts, setClearedAlerts] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("wibil_cleared_alerts") || "[]");
    } catch { return []; }
  });

  // Alerts — only flag truly absent employees (missed 10 AM deadline), NOT those who checked out
  const rawAlerts = [
    ...team.filter((e) => isEmpAbsent(e)).map((e) => ({
      id: `absent-${e.employeeId || e.name}`,
      type: "absent", name: e.name, msg: `Not checked in · Last seen ${formatClientDisplayTime(e.lastSeen)}`,
    })),
    ...team.filter((e) => e.onOD && (e.odHistory || []).some((od) => !od.arrived)).map((e) => ({
      id: `od-${e.employeeId || e.name}`,
      type: "od-pending", name: e.name, msg: `On OD in ${e.odCity} — arrival not confirmed`,
    })),
  ];

  const alerts = rawAlerts.filter((a) => !clearedAlerts.includes(a.id));

  const handleClearAllAlerts = () => {
    const allIds = rawAlerts.map((a) => a.id);
    const updated = [...new Set([...clearedAlerts, ...allIds])];
    setClearedAlerts(updated);
    try { localStorage.setItem("wibil_cleared_alerts", JSON.stringify(updated)); } catch {}
  };

  const handleDismissSingleAlert = (id) => {
    const updated = [...clearedAlerts, id];
    setClearedAlerts(updated);
    try { localStorage.setItem("wibil_cleared_alerts", JSON.stringify(updated)); } catch {}
  };

  const [exportModal, setExportModal] = useState(false);

  const handleDownloadReport = (type) => {
    const token = localStorage.getItem("fp_token");
    window.open(`https://api.goldpe.tech/api/export/${type}?token=${token || ""}`, "_blank");
  };

  return (
    <div style={{ paddingBottom: 32 }}>
      {/* Top Banner */}
      <Card style={{
        background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
        color: "#fff", marginBottom: 20, position: "relative", overflow: "hidden", padding: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: `${TOKENS.gold}22`, border: `1.5px solid ${TOKENS.gold}`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <TrendingUp size={24} color={TOKENS.gold} />
            </div>
            <div>
              <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: "#fff", margin: 0 }}>
                Analytics & Leaderboard
              </h2>
              <p style={{ fontSize: 12.5, color: TOKENS.goldLight, margin: "4px 0 0", fontWeight: 500 }}>
                Real-time field operations, performance index, manager ratings & statistics
              </p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => setExportModal(true)} style={{
              background: TOKENS.gold, border: "none",
              borderRadius: 10, padding: "8px 14px", color: TOKENS.navyDeep, fontSize: 12, fontWeight: 700,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            }}>
              📊 Export Excel Reports
            </button>

            <button onClick={fetchAnalytics} disabled={loading} style={{
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 10, padding: "8px 14px", color: "#fff", fontSize: 12, fontWeight: 600,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            }}>
              <RefreshCw size={14} className={loading ? "spin" : ""} /> Refresh
            </button>
          </div>
        </div>
      </Card>

      {/* KPI Tiles Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Team Avg Score",  value: `${summary.avgPerformanceScore}%`, color: TOKENS.gold,     sub: "Overall performance", icon: "📊" },
          { label: "Avg Star Rating", value: `${summary.avgManagerRating} ★`,   color: TOKENS.gold,     sub: "Manager evaluations",  icon: "⭐" },
          { label: "Task Completion", value: `${summary.taskCompletionRate}%`,  color: TOKENS.success,  sub: `${summary.totalTasksCompleted}/${summary.totalTasksAssigned || 0} done`, icon: "✅" },
          { label: "Punctuality",     value: `${summary.punctualityRate}%`,     color: TOKENS.blue,     sub: `${punctuality.onTimeCount} on-time checkins`, icon: "⏱️" },
          { label: "Active On Duty",  value: `${checkedInCount + onODCount}/${team.length}`, color: TOKENS.success, sub: `${checkedInCount} in · ${onODCount} OD`, icon: "🟢" },
          { label: "Daily Reports",   value: `${dailyReportCount}/${team.length}`, color: TOKENS.navyDeep, sub: "Submitted today",     icon: "📝" },
          { label: "Lunch Requests",  value: `${lunchRequests}`,               color: TOKENS.gold,     sub: "Requests for today",    icon: "🍱" },
        ].map(({ label, value, color, sub, icon }) => (
          <Card key={label} style={{ padding: "14px 16px", textAlign: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>{icon}</div>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 24, fontWeight: 800, color }}>{value}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.ink, marginTop: 4 }}>{label}</div>
            <div style={{ fontSize: 10.5, color: TOKENS.muted, marginTop: 2 }}>{sub}</div>
          </Card>
        ))}
      </div>

      {/* WhatsApp Daily Summary Widget */}
      <Card style={{ padding: 20, marginBottom: 20, background: TOKENS.cream, border: `1px solid ${TOKENS.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: "50%",
              background: "#25D366", display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 10px rgba(37,211,102,0.28)",
            }}>
              <MessageCircle size={18} color="#fff" fill="#fff" />
            </div>
            <div>
              <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 700, color: TOKENS.navyDeep, margin: 0 }}>
                WhatsApp Daily Summary (9:00 PM Status)
              </h3>
              <p style={{ fontSize: 11, color: TOKENS.muted, margin: "2px 0 0" }}>
                Send daily status reports (check-in, check-out, OD, daily reports) directly to all employees.
              </p>
            </div>
          </div>
          <button
            onClick={handleSendAll}
            disabled={sendingAll}
            style={{
              background: "#25D366", color: "#fff", border: "none",
              borderRadius: 10, padding: "8px 16px", fontSize: 12, fontWeight: 700,
              cursor: sendingAll ? "default" : "pointer",
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: "0 4px 12px rgba(37,211,102,0.25)",
              opacity: sendingAll ? 0.7 : 1,
            }}
          >
            {sendingAll ? "Sending…" : "Send All Statuses"}
          </button>
        </div>

        {waNotice && (
          <div style={{
            marginTop: 12, padding: "8px 12px", borderRadius: 10, fontSize: 12, fontWeight: 600,
            background: TOKENS.successBg, color: TOKENS.success, border: `1px solid ${TOKENS.success}33`,
          }}>
            {waNotice}
          </div>
        )}
      </Card>

      {/* Prominent Incident Dashboard Card */}
      <div
        onClick={() => onNavigate && onNavigate("incidents")}
        style={{
          marginBottom: 20,
          borderRadius: 18,
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 0.2s ease",
          boxShadow: incidentCounts.pending > 0
            ? `0 4px 20px rgba(239,68,68,0.15)`
            : `0 4px 16px rgba(10,31,58,0.08)`,
          border: incidentCounts.pending > 0
            ? `2px solid ${TOKENS.danger}40`
            : `1.5px solid ${TOKENS.border}`,
          background: "#fff",
        }}
        className="hover-card"
      >
        {/* Card Header */}
        <div style={{
          background: incidentCounts.pending > 0
            ? `linear-gradient(135deg, #7f1d1d, #991b1b)`
            : `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 12,
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20,
            }}>
              ⚠️
            </div>
            <div>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 17, fontWeight: 700, color: "#fff" }}>
                Incident Register
              </div>
              <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.7)", fontWeight: 500, marginTop: 2 }}>
                {incidentCounts.pending > 0
                  ? `🔴 ${incidentCounts.pending} incident${incidentCounts.pending > 1 ? "s" : ""} awaiting your review`
                  : "All incidents reviewed ✓"}
              </div>
            </div>
          </div>
          <div style={{
            background: "rgba(255,255,255,0.15)",
            border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: 10,
            padding: "8px 14px",
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
            View All →
          </div>
        </div>

        {/* Stats Grid */}
        <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 12 }}>
          <div style={{ textAlign: "center", padding: "10px 8px", borderRadius: 12, background: TOKENS.cream, border: `1px solid ${TOKENS.border}` }}>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 800, color: TOKENS.navyDeep }}>
              {incidentCounts.total}
            </div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>
              Total Logged
            </div>
          </div>

          <div style={{ textAlign: "center", padding: "10px 8px", borderRadius: 12, background: TOKENS.successBg, border: `1px solid ${TOKENS.success}25` }}>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 800, color: TOKENS.success }}>
              {incidentCounts.solved}
            </div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.success, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>
              Solved
            </div>
          </div>

          <div style={{ textAlign: "center", padding: "10px 8px", borderRadius: 12, background: TOKENS.dangerBg, border: `1px solid ${TOKENS.danger}25` }}>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 800, color: TOKENS.danger }}>
              {incidentCounts.unsolved}
            </div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.danger, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>
              Unsolved
            </div>
          </div>

          <div style={{ textAlign: "center", padding: "10px 8px", borderRadius: 12, background: TOKENS.warningBg || "#FFF7ED", border: `1px solid ${TOKENS.warning}25` }}>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 800, color: TOKENS.warning }}>
              {incidentCounts.pending}
            </div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.warning, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>
              Pending Review
            </div>
          </div>

          <div style={{ textAlign: "center", padding: "10px 8px", borderRadius: 12, background: "#F0FDF4", border: `1px solid ${TOKENS.success}20` }}>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 800, color: TOKENS.success }}>
              {incidentCounts.approved}
            </div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.success, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>
              Approved
            </div>
          </div>

          <div style={{ textAlign: "center", padding: "10px 8px", borderRadius: 12, background: "#FEF2F2", border: `1px solid ${TOKENS.danger}20` }}>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 800, color: TOKENS.danger }}>
              {incidentCounts.rejected}
            </div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.danger, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>
              Disapproved
            </div>
          </div>
        </div>
      </div>

      {/* 5-Min Break Blitz & Team Leaderboard Widget for Manager */}
      <Card style={{
        marginBottom: 20,
        padding: 20,
        background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
        color: "#fff",
        borderRadius: 18,
        border: `1.5px solid rgba(201, 162, 39, 0.35)`,
        boxShadow: "0 6px 20px rgba(10, 31, 58, 0.25)"
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: "rgba(201, 162, 39, 0.2)", border: `1px solid ${TOKENS.gold}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: TOKENS.goldLight
            }}>
              <Gamepad2 size={24} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, margin: 0, color: "#fff" }}>
                  5-Min Break Blitz & Team Leaderboard
                </h3>
                <span style={{ fontSize: 10, background: "rgba(201,162,39,0.25)", color: TOKENS.goldLight, padding: "2px 8px", borderRadius: 10, fontWeight: 700 }}>
                  TEAM RECHARGE
                </span>
              </div>
              <p style={{ fontSize: 12, color: "#9FB0C9", margin: "2px 0 0" }}>
                Encourage employee wellness with 5-minute break games. Managers can join too!
              </p>
            </div>
          </div>

          <button
            onClick={() => setGameModalOpen(true)}
            style={{
              background: `linear-gradient(135deg, #ffe066 0%, #f5b800 50%, #d49b00 100%)`,
              color: TOKENS.navyDeep,
              border: "none",
              borderRadius: 12,
              padding: "10px 18px",
              fontWeight: 800,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(245, 184, 0, 0.35)"
            }}
          >
            <Sparkles size={16} /> Play 5-Min Break
          </button>
        </div>

        {/* Top 3 Team Leaderboard */}
        <div style={{
          background: "rgba(7, 19, 37, 0.5)",
          borderRadius: 12,
          padding: 14,
          border: "1px solid rgba(255, 255, 255, 0.08)"
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: TOKENS.goldLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <Trophy size={14} color={TOKENS.gold} /> Team Top Break Gamers
          </div>

          {gameLeaderboard && gameLeaderboard.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
              {gameLeaderboard.slice(0, 3).map((item, idx) => (
                <div key={idx} style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  borderRadius: 10,
                  padding: "10px 12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: `1px solid ${idx === 0 ? TOKENS.gold + "50" : "rgba(255, 255, 255, 0.08)"}`
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{
                      fontWeight: 900,
                      fontSize: 14,
                      color: idx === 0 ? TOKENS.gold : idx === 1 ? "#C0C0C0" : "#CD7F32"
                    }}>
                      #{idx + 1}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>
                      {item.name || item.employee_id}
                    </span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 800, color: TOKENS.goldLight }}>
                    {item.top_score} pts
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "#8CA3BA", fontStyle: "italic", textAlign: "center", padding: "6px 0" }}>
              No break game scores recorded yet today. Be the first to set the high score! 🚀
            </div>
          )}
        </div>
      </Card>

      {/* LOP Cancellation Requests */}
      {lopRequests.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <SectionLabel>📋 Pending LOP Cancellation / Late Check-In Requests ({lopRequests.length})</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {lopRequests.map((req) => (
              <Card key={req.id} style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", borderLeft: `4px solid ${TOKENS.gold}` }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: TOKENS.navyDeep, color: TOKENS.gold, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                      {req.initials || "EM"}
                    </div>
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 700, color: TOKENS.navyDeep }}>{req.employee_name}</span>
                      <span style={{ fontSize: 11, color: TOKENS.muted, marginLeft: 6 }}>({req.team_name} · {req.employee_id})</span>
                    </div>
                  </div>
                  <div style={{ fontSize: 12.5, color: TOKENS.ink, marginTop: 8, lineHeight: 1.4 }}>
                    <strong>Reason:</strong> "{req.reason}"
                  </div>
                  <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 4 }}>
                    Requested today at {req.check_in_time} IST
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => handleReviewLop(req.id, "approved")}
                    disabled={lopLoadingId === req.id}
                    style={{
                      background: TOKENS.success, color: "#fff", border: "none",
                      borderRadius: 10, padding: "8px 14px", fontSize: 12, fontWeight: 700,
                      cursor: "pointer", boxShadow: "0 2px 8px rgba(95,214,139,0.25)"
                    }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleReviewLop(req.id, "rejected")}
                    disabled={lopLoadingId === req.id}
                    style={{
                      background: TOKENS.danger, color: "#fff", border: "none",
                      borderRadius: 10, padding: "8px 14px", fontSize: 12, fontWeight: 700,
                      cursor: "pointer", boxShadow: "0 2px 8px rgba(232,137,111,0.25)"
                    }}
                  >
                    Reject
                  </button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Active Alerts */}
      {alerts.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <SectionLabel style={{ margin: 0 }}>⚠️ Active alerts ({alerts.length})</SectionLabel>
            <button
              onClick={handleClearAllAlerts}
              style={{
                background: TOKENS.dangerBg,
                border: `1px solid ${TOKENS.danger}40`,
                color: TOKENS.danger,
                fontSize: 11.5,
                fontWeight: 700,
                padding: "4px 10px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Trash2 size={13} /> Clear All
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {alerts.map((a, i) => (
              <div key={a.id || i} style={{
                background: a.type === "absent" ? TOKENS.dangerBg : TOKENS.warningBg,
                border: `1px solid ${a.type === "absent" ? `${TOKENS.danger}40` : `${TOKENS.warning}40`}`,
                borderRadius: 12, padding: "10px 14px",
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <AlertTriangle size={16} color={a.type === "absent" ? TOKENS.danger : TOKENS.warning} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.ink }}>{a.name}</div>
                    <div style={{ fontSize: 11.5, color: TOKENS.muted }}>{a.msg}</div>
                  </div>
                </div>
                <button
                  onClick={() => handleDismissSingleAlert(a.id)}
                  title="Clear alert"
                  style={{
                    background: "transparent", border: "none", color: TOKENS.muted,
                    cursor: "pointer", padding: 4, display: "flex", alignItems: "center"
                  }}
                >
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance Rankings Table */}
      <div style={{ marginBottom: 24 }}>
        <SectionLabel>🏆 Team Performance Leaderboard & Statistics</SectionLabel>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
              <thead>
                <tr style={{ background: TOKENS.navyDeep, color: "#fff", fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  <th style={{ padding: "12px 16px" }}>Agent Name</th>
                  <th style={{ padding: "12px 12px", textAlign: "center" }}>Score</th>
                  <th style={{ padding: "12px 12px", textAlign: "center" }}>Grade</th>
                  <th style={{ padding: "12px 12px", textAlign: "center" }}>Incentive / Action</th>
                  <th style={{ padding: "12px 12px", textAlign: "center" }}>Rating</th>
                  <th style={{ padding: "12px 12px", textAlign: "center" }}>Tasks Done</th>
                  <th style={{ padding: "12px 16px", textAlign: "right" }}>Status Tier</th>
                </tr>
              </thead>
              <tbody>
                {[...employeesPerformance].sort((a, b) => b.score - a.score).map((emp, i) => (
                  <tr
                    key={emp.employeeId || i}
                    onClick={() => onSelectEmp && onSelectEmp(emp.employeeId)}
                    style={{
                      borderBottom: `1px solid ${TOKENS.border}`,
                      cursor: "pointer",
                      transition: "background 0.15s ease",
                      background: i % 2 === 0 ? "transparent" : "rgba(10,25,50,0.015)",
                    }}
                    className="hover-row"
                  >
                    <td style={{ padding: "14px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: "50%",
                          background: i === 0 ? TOKENS.gold : i === 1 ? "#94A3B8" : i === 2 ? "#D97706" : TOKENS.navyMid,
                          color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 12, fontWeight: 800, flexShrink: 0,
                        }}>
                          {i + 1}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: TOKENS.ink }}>{emp.name}</div>
                          <div style={{ fontSize: 11, color: TOKENS.muted }}>{emp.role}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "14px 12px", textAlign: "center" }}>
                      <span style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 800, color: TOKENS.gold }}>
                        {emp.score}%
                      </span>
                    </td>
                    <td style={{ padding: "14px 12px", textAlign: "center" }}>
                      <span style={{
                        display: "inline-block", padding: "3px 10px", borderRadius: 8, fontSize: 12, fontWeight: 800,
                        background: emp.grade === "A+" || emp.score >= 90 ? "#FEF3C7" : emp.grade === "A" || emp.score >= 80 ? "#DBEAFE" : emp.grade === "B" || emp.score >= 70 ? "#E0E7FF" : emp.grade === "C" ? "#FFEDD5" : "#FEE2E2",
                        color: emp.grade === "A+" || emp.score >= 90 ? "#B45309" : emp.grade === "A" || emp.score >= 80 ? "#1E40AF" : emp.grade === "B" || emp.score >= 70 ? "#3730A3" : emp.grade === "C" ? "#C2410C" : "#991B1B",
                        border: `1px solid ${emp.score >= 90 ? "#F59E0B" : emp.score >= 80 ? "#3B82F6" : emp.score >= 70 ? "#6366F1" : "#EF4444"}`
                      }}>
                        {emp.grade || (emp.score >= 90 ? "A+" : emp.score >= 80 ? "A" : emp.score >= 70 ? "B" : emp.score >= 60 ? "C" : "D")}
                      </span>
                    </td>
                    <td style={{ padding: "14px 12px", textAlign: "center" }}>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: TOKENS.ink }}>
                        {emp.action || (emp.score >= 90 ? "Incentive eligible, top 10% pool" : emp.score >= 80 ? "Meets expectations, full variable pay" : emp.score >= 70 ? "Coaching conversation, 75% variable" : emp.score >= 60 ? "Written improvement plan, 30 days" : "PIP, no variable pay")}
                      </span>
                    </td>
                    <td style={{ padding: "14px 12px", textAlign: "center" }}>
                      <span style={{ fontWeight: 700, color: TOKENS.ink }}>
                        {emp.avgRating ? `${emp.avgRating} ★` : "5.0 ★"}
                      </span>
                    </td>
                    <td style={{ padding: "14px 12px", textAlign: "center" }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: TOKENS.ink }}>
                        {emp.tasksCompleted}/{emp.tasksAssigned}
                      </span>
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "right" }}>
                      <span style={{
                        display: "inline-block", padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: emp.score >= 90 ? "rgba(245,184,0,0.15)" : emp.score >= 80 ? `${TOKENS.blue}15` : `${TOKENS.success}15`,
                        color: emp.score >= 90 ? TOKENS.gold : emp.score >= 80 ? TOKENS.blue : TOKENS.success,
                        border: `1px solid ${emp.score >= 90 ? `${TOKENS.gold}40` : emp.score >= 80 ? `${TOKENS.blue}40` : `${TOKENS.success}40`}`,
                      }}>
                        {emp.performanceTier}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Ratings & Punctuality 2-Column Analytics */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {/* Ratings Breakdown */}
        <Card style={{ padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <Star size={18} color={TOKENS.gold} fill={TOKENS.gold} />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: TOKENS.ink, margin: 0 }}>
              Manager Ratings Distribution
            </h3>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { label: "5 Stars (Excellent)", count: ratingDist.fiveStar, color: TOKENS.gold },
              { label: "4 Stars (Good)",      count: ratingDist.fourStar, color: TOKENS.success },
              { label: "3 Stars (Average)",   count: ratingDist.threeStar, color: TOKENS.blue },
              { label: "2 Stars (Needs Work)", count: ratingDist.twoStar, color: TOKENS.warning },
              { label: "1 Star (Poor)",       count: ratingDist.oneStar, color: TOKENS.danger },
            ].map(({ label, count, color }) => {
              const pct = Math.round((count / totalRatings) * 100);
              return (
                <div key={label}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, fontWeight: 600, color: TOKENS.ink, marginBottom: 4 }}>
                    <span>{label}</span>
                    <span>{count} ({pct}%)</span>
                  </div>
                  <div style={{ height: 7, background: "rgba(0,0,0,0.06)", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.5s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Punctuality Statistics */}
        <Card style={{ padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <Clock size={18} color={TOKENS.blue} />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: TOKENS.ink, margin: 0 }}>
              Punctuality Breakdown
            </h3>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, textAlign: "center", marginBottom: 14 }}>
            <div style={{ background: TOKENS.successBg, padding: 10, borderRadius: 10, border: `1px solid ${TOKENS.success}30` }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: TOKENS.success }}>{punctuality.onTimeCount}</div>
              <div style={{ fontSize: 10, color: TOKENS.muted, fontWeight: 600 }}>On Time (≤ 9:30)</div>
            </div>
            <div style={{ background: TOKENS.warningBg, padding: 10, borderRadius: 10, border: `1px solid ${TOKENS.warning}30` }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: TOKENS.warning }}>{punctuality.graceCount}</div>
              <div style={{ fontSize: 10, color: TOKENS.muted, fontWeight: 600 }}>Grace (9:30-9:45)</div>
            </div>
            <div style={{ background: TOKENS.dangerBg, padding: 10, borderRadius: 10, border: `1px solid ${TOKENS.danger}30` }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: TOKENS.danger }}>{punctuality.lateCount}</div>
              <div style={{ fontSize: 10, color: TOKENS.muted, fontWeight: 600 }}>Late (&gt; 9:45)</div>
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: TOKENS.muted, textAlign: "center", fontWeight: 500 }}>
            Average check-in delay: <strong style={{ color: TOKENS.ink }}>{punctuality.averageDelayMinutes || 0} mins</strong>
          </div>
        </Card>
      </div>

      {/* City Distribution Chart */}
      <SectionLabel>Agent Location Distribution</SectionLabel>
      <Card style={{ padding: "18px 12px 12px" }}>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={cityData} barSize={32}>
            <CartesianGrid vertical={false} stroke="#F0ECE1" />
            <XAxis dataKey="city" tick={{ fontSize: 11, fill: TOKENS.muted }} axisLine={false} tickLine={false} />
            <YAxis hide allowDecimals={false} />
            <Tooltip contentStyle={{ fontFamily: "Inter", fontSize: 12, borderRadius: 10, border: `1px solid ${TOKENS.border}` }} />
            <Bar dataKey="count" radius={[6, 6, 0, 0]} fill={TOKENS.navyMid} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      {/* Excel Report Export Modal */}
      {exportModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(10,20,40,0.65)", display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 999, padding: 16
        }}>
          <div style={{
            background: "#fff", borderRadius: 20, padding: 22, maxWidth: 440, width: "100%",
            boxShadow: "0 20px 40px rgba(0,0,0,0.25)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 19, fontWeight: 700, color: TOKENS.navyDeep, margin: 0 }}>
                  📥 Download Overall Excel Reports
                </h3>
                <div style={{ fontSize: 12, color: TOKENS.muted, marginTop: 2 }}>
                  Generated in UTF-8 CSV format for direct opening in Microsoft Excel.
                </div>
              </div>
              <button onClick={() => setExportModal(false)} style={{ background: "transparent", border: "none", cursor: "pointer", color: TOKENS.muted }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
              {[
                { type: "performance-grades", label: "🏆 Grade Bands & Variable Pay Incentives Report", desc: "Detailed formula breakdown (0.20A + 0.10P + 0.15R + 0.20U + 0.10O + 0.25C), Grade (A+/A/B/C/D), Penalties, and Variable Pay Actions" },
                { type: "overall", label: "📊 Overall Master Company Report", desc: "Combined employee scores, tasks, attendance, reports, CL balances, and approved expenses" },
                { type: "reimbursements", label: "💳 Reimbursement Claims", desc: "Detailed breakdown of all employee reimbursement claims, categories, approval status & amounts" },
                { type: "attendance", label: "📅 Master Attendance & GPS Check-ins", desc: "Daily attendance logs, check-in/out timestamps, and reverse-geocoded GPS locations" },
                { type: "tasks", label: "📋 Task Logs & Manager Ratings", desc: "Task assignments, completion times, location, manager star ratings, and feedback" },
                { type: "daily-reports", label: "📄 Everyday Work Reports Master Log", desc: "Everyday work descriptions, hours logged, manager ratings, and review status" },
                { type: "leaves", label: "🌴 Casual Leave (CL) & Leave Ledger", desc: "Total allowed CL, used CL days, remaining CL days, and medical leave allocation" },
              ].map((item) => (
                <button
                  key={item.type}
                  onClick={() => handleDownloadReport(item.type)}
                  style={{
                    background: TOKENS.cream,
                    border: `1.5px solid ${TOKENS.border}`,
                    borderRadius: 12,
                    padding: "12px 14px",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 13.5, color: TOKENS.navyDeep }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 2 }}>
                    {item.desc}
                  </div>
                </button>
              ))}
            </div>

            <button
              onClick={() => setExportModal(false)}
              style={{
                width: "100%", padding: "11px 0", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`,
                background: "#fff", color: TOKENS.ink, fontWeight: 700, fontSize: 13, cursor: "pointer"
              }}
            >
              Close Window
            </button>
          </div>
        </div>
      )}

      <BreakGameModal
        isOpen={gameModalOpen}
        onClose={() => setGameModalOpen(false)}
        onScoreUpdated={(data) => {
          fetchGameLeaderboard();
        }}
      />
    </div>
  );
}
